import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { RolePersona } from "../../../types/apiTypes";
import { TriangleIcon } from "../../images";

const BASE_COLOR = "#778AA7";
const UNASSIGNED_LABEL = "No Persona";

const personaColor = (index: number) =>
  `color-mix(in srgb, #FFF ${index * 5}%, ${BASE_COLOR})`;

const captionStyle = {
  color: "var(--text-default-caption, #797979)",
  fontSize: "12px",
  lineHeight: "18px",
  letterSpacing: "0.3px",
};

const percent = (value: number, total: number) =>
  total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;

type Segment = {
  key: string;
  color: string;
  width: number;
  percent: number;
  label: string;
};

const LegendItem = ({ segment }: { segment: Segment }) => (
  <Stack sx={{ alignSelf: "stretch" }}>
    <Stack
      sx={{
        padding: "0 2px",
        flexDirection: "row",
        alignItems: "center",
        gap: "4px",
        borderRadius: "2px",
      }}
    >
      <Stack
        sx={{ width: "10px", height: "10px", background: segment.color }}
      />
      <Typography
        sx={{
          color: "var(--text-default-heading, #232323)",
          fontFeatureSettings: "'liga' off, 'clig' off",
          fontSize: "12px",
          lineHeight: "14px",
          letterSpacing: "0.4px",
        }}
      >
        {segment.percent}%
      </Typography>
    </Stack>
    <Typography
      sx={{
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: "var(--text-default-body, #464646)",
        fontSize: "12px",
        lineHeight: "18px",
        letterSpacing: "0.3px",
      }}
    >
      {segment.label}
    </Typography>
  </Stack>
);

type Props = {
  personas: RolePersona[];
  unassignedCount: number;
  expectedCount: number | null;
};

export const RoleCoverage = (props: Props) => {
  const { personas, unassignedCount, expectedCount } = props;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const targetedTotal =
    personas.length > 0 ? personas.length : (expectedCount ?? 0);
  const totalExpected =
    personas.length > 0
      ? personas.reduce((acc, persona) => acc + persona.expected_count, 0)
      : (expectedCount ?? 0);
  const personasActual = personas.reduce(
    (acc, persona) => acc + persona.actual_count,
    0,
  );
  const totalActual = personasActual + unassignedCount;

  const targetedCount = personas.filter(
    (persona) =>
      persona.expected_count > 0 &&
      persona.actual_count >= persona.expected_count,
  ).length;

  const segments: Segment[] = personas.map((persona, index) => ({
    key: persona.id,
    color: personaColor(index),
    width: percent(persona.actual_count, totalExpected),
    percent: percent(persona.actual_count, persona.expected_count),
    label: persona.name,
  }));

  if (unassignedCount > 0) {
    segments.push({
      key: "unassigned",
      color: personaColor(personas.length),
      width: totalExpected > 0 ? percent(unassignedCount, totalExpected) : 100,
      percent:
        totalExpected > 0 ? percent(unassignedCount, totalExpected) : 100,
      label: UNASSIGNED_LABEL,
    });
  }

  return (
    <Stack
      sx={{
        padding: "12px",
        alignItems: "flex-start",
        gap: "12px",
        alignSelf: "stretch",
        borderRadius: "8px",
        background: "var(--surface-text-field, #FBFBFB)",
      }}
    >
      <Stack
        sx={{
          height: "18px",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
        }}
      >
        <Typography sx={captionStyle}>
          {targetedCount} of {targetedTotal} targeted
        </Typography>
        <Typography sx={captionStyle}>
          {percent(personasActual, totalExpected)}%
        </Typography>
      </Stack>

      <Stack
        sx={{
          width: "161px",
          height: "10px",
          flexDirection: "row",
          borderRadius: "8px",
          border: "1px solid var(--Neutral-50, #EEE)",
          background: "var(--Neutral-25, #F7F7F7)",
          overflow: "hidden",
        }}
      >
        {segments.map((segment) => (
          <Stack
            key={segment.key}
            sx={{ width: `${segment.width}%`, background: segment.color }}
          />
        ))}
      </Stack>

      {isExpanded && (
        <Stack sx={{ alignSelf: "stretch", gap: "8px" }}>
          {segments.map((segment) => (
            <LegendItem key={segment.key} segment={segment} />
          ))}
        </Stack>
      )}

      {totalActual > 0 && (
        <Stack
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            cursor: "pointer",
            flexDirection: "row",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Typography
            sx={{
              color: "var(--text-action-primary-default, #1C3C6C)",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "26px",
              letterSpacing: "0.25px",
              textTransform: "capitalize",
            }}
          >
            {isExpanded ? "Hide info" : "Show more info"}
          </Typography>
          <TriangleIcon color="#1C3C6C" rotate={isExpanded ? "180" : "0"} />
        </Stack>
      )}
    </Stack>
  );
};
